/**
 * Admin list/CRUD services for tracking entities (trip-students-can, trip-stops, etc.).
 * Base URL: api/v1/
 */
import { createTrackingClient } from "../trackingApi";

function getClient(accessToken: string | undefined) {
  return createTrackingClient(accessToken);
}

function toList<T>(data: T[] | { results: T[] }): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "results" in data) return (data as { results: T[] }).results;
  return [];
}

// --- Trip student scans ---
export interface TripStudentScanRow {
  id: string;
  trip_id: string;
  student_id: number;
  scan_type: string;
  scanned_at: string;
  created_at: string;
}
export async function fetchTripStudentsCan(accessToken: string | undefined): Promise<TripStudentScanRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<TripStudentScanRow[] | { results: TripStudentScanRow[] }>("/api/v1/trip-students-can/");
  return toList(data);
}

// --- Trip stops ---
export interface TripStopRow {
  id: string;
  trip_id: string;
  student_id: number;
  order: number;
  latitude: string;
  longitude: string;
  label: string | null;
  is_locked: boolean;
  created_at: string;
}
export async function fetchTripStops(accessToken: string | undefined): Promise<TripStopRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<TripStopRow[] | { results: TripStopRow[] }>("/api/v1/trip-stops/");
  return toList(data);
}

// --- Trip positions ---
export interface TripPositionRow {
  id: string;
  trip_id: string;
  latitude: string;
  longitude: string;
  recorded_at: string;
  created_at: string;
}
export async function fetchTripPositions(accessToken: string | undefined): Promise<TripPositionRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<TripPositionRow[] | { results: TripPositionRow[] }>("/api/v1/trip-positions/");
  return toList(data);
}

// --- Student presence ---
export interface StudentPresenceRow {
  id: string;
  trip_id: string;
  student_id: number;
  status: string;
  scanned_at: string | null;
  created_at: string;
}
export async function fetchStudentPresence(accessToken: string | undefined): Promise<StudentPresenceRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<StudentPresenceRow[] | { results: StudentPresenceRow[] }>("/api/v1/student-presence/");
  return toList(data);
}

// --- Student unavailability history (CRUD) ---
export interface StudentUnavailabilityHistoryRow {
  id: string;
  student_id: number;
  parent_id: number | null;
  reason: string;
  start_date: string;
  end_date: string | null;
  created_at: string;
}
export interface StudentUnavailabilityHistoryPayload {
  student_id: number;
  parent_id?: number | null;
  reason: string;
  start_date: string;
  end_date?: string | null;
}
export async function fetchStudentUnavailabilityHistory(accessToken: string | undefined): Promise<StudentUnavailabilityHistoryRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<StudentUnavailabilityHistoryRow[] | { results: StudentUnavailabilityHistoryRow[] }>("/api/v1/student-unavailability-history/");
  return toList(data);
}
export async function createStudentUnavailabilityHistory(accessToken: string | undefined, payload: StudentUnavailabilityHistoryPayload): Promise<StudentUnavailabilityHistoryRow> {
  const client = getClient(accessToken);
  const { data } = await client.post<StudentUnavailabilityHistoryRow>("/api/v1/student-unavailability-history/", payload);
  return data;
}
export async function updateStudentUnavailabilityHistory(accessToken: string | undefined, id: string, payload: Partial<StudentUnavailabilityHistoryPayload>): Promise<StudentUnavailabilityHistoryRow> {
  const client = getClient(accessToken);
  const { data } = await client.patch<StudentUnavailabilityHistoryRow>(`/api/v1/student-unavailability-history/${id}/`, payload);
  return data;
}
export async function deleteStudentUnavailabilityHistory(accessToken: string | undefined, id: string): Promise<void> {
  const client = getClient(accessToken);
  await client.delete(`/api/v1/student-unavailability-history/${id}/`);
}

// --- SLA config (CRUD) ---
export interface SLAConfigRow {
  id: string;
  name: string;
  severity: string | null;
  incident_type_id: string | null;
  max_response_minutes: number;
  max_resolution_minutes: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}
export interface SLAConfigPayload {
  name: string;
  severity?: string | null;
  incident_type_id?: string | null;
  max_response_minutes: number;
  max_resolution_minutes?: number | null;
  is_active?: boolean;
}
export async function fetchSLAConfig(accessToken: string | undefined): Promise<SLAConfigRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<SLAConfigRow[] | { results: SLAConfigRow[] }>("/api/v1/sla-config/");
  return toList(data);
}
export async function createSLAConfig(accessToken: string | undefined, payload: SLAConfigPayload): Promise<SLAConfigRow> {
  const client = getClient(accessToken);
  const { data } = await client.post<SLAConfigRow>("/api/v1/sla-config/", payload);
  return data;
}
export async function updateSLAConfig(accessToken: string | undefined, id: string, payload: Partial<SLAConfigPayload>): Promise<SLAConfigRow> {
  const client = getClient(accessToken);
  const { data } = await client.patch<SLAConfigRow>(`/api/v1/sla-config/${id}/`, payload);
  return data;
}
export async function deleteSLAConfig(accessToken: string | undefined, id: string): Promise<void> {
  const client = getClient(accessToken);
  await client.delete(`/api/v1/sla-config/${id}/`);
}

// --- Guard companies (CRUD) ---
export interface GuardCompanyRow {
  id: string;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  manager_id: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}
export interface GuardCompanyPayload {
  name: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  manager_id?: number | null;
  is_active?: boolean;
}
export async function fetchGuardCompanies(accessToken: string | undefined): Promise<GuardCompanyRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardCompanyRow[] | { results: GuardCompanyRow[] }>("/api/v1/guard/companies/");
  return toList(data);
}
export async function createGuardCompany(accessToken: string | undefined, payload: GuardCompanyPayload): Promise<GuardCompanyRow> {
  const client = getClient(accessToken);
  const { data } = await client.post<GuardCompanyRow>("/api/v1/guard/companies/", payload);
  return data;
}
export async function updateGuardCompany(accessToken: string | undefined, id: string, payload: Partial<GuardCompanyPayload>): Promise<GuardCompanyRow> {
  const client = getClient(accessToken);
  const { data } = await client.patch<GuardCompanyRow>(`/api/v1/guard/companies/${id}/`, payload);
  return data;
}
export async function deleteGuardCompany(accessToken: string | undefined, id: string): Promise<void> {
  const client = getClient(accessToken);
  await client.delete(`/api/v1/guard/companies/${id}/`);
}

// --- Guard posts (CRUD) ---
export interface GuardPostRow {
  id: string;
  site_id: string;
  name: string;
  qr_code: string | null;
  order: number;
  created_at: string;
  updated_at: string;
}
export interface GuardPostPayload {
  site_id: string;
  name: string;
  qr_code?: string | null;
  order?: number;
}
export async function fetchGuardPosts(accessToken: string | undefined): Promise<GuardPostRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<GuardPostRow[] | { results: GuardPostRow[] }>("/api/v1/guard/posts/");
  return toList(data);
}
export async function createGuardPost(accessToken: string | undefined, payload: GuardPostPayload): Promise<GuardPostRow> {
  const client = getClient(accessToken);
  const { data } = await client.post<GuardPostRow>("/api/v1/guard/posts/", payload);
  return data;
}
export async function updateGuardPost(accessToken: string | undefined, id: string, payload: Partial<GuardPostPayload>): Promise<GuardPostRow> {
  const client = getClient(accessToken);
  const { data } = await client.patch<GuardPostRow>(`/api/v1/guard/posts/${id}/`, payload);
  return data;
}
export async function deleteGuardPost(accessToken: string | undefined, id: string): Promise<void> {
  const client = getClient(accessToken);
  await client.delete(`/api/v1/guard/posts/${id}/`);
}

// --- Patrol checks (list) ---
export interface PatrolCheckRow {
  id: string;
  patrol_id: string;
  checkpoint_id: string;
  checked_at: string;
  latitude: string | null;
  longitude: string | null;
  comment: string | null;
  agent_present: boolean;
  created_at: string;
}
export async function fetchPatrolChecks(accessToken: string | undefined): Promise<PatrolCheckRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<PatrolCheckRow[] | { results: PatrolCheckRow[] }>("/api/v1/patrol-checks/");
  return toList(data);
}

// --- Incident media (list) ---
export interface IncidentMediaRow {
  id: string;
  incident_id: string;
  file: string;
  media_type: string;
  order: number;
  created_at: string;
}
export async function fetchIncidentMedia(accessToken: string | undefined): Promise<IncidentMediaRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<IncidentMediaRow[] | { results: IncidentMediaRow[] }>("/api/v1/incident-media/");
  return toList(data);
}

// --- Incident types (CRUD) - api/v1/incident-types/ ---
export interface IncidentTypeRow {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}
export interface IncidentTypePayload {
  name: string;
  code: string;
  is_active?: boolean;
}
export async function fetchIncidentTypes(accessToken: string | undefined): Promise<IncidentTypeRow[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<IncidentTypeRow[] | { results: IncidentTypeRow[] }>("/api/v1/incident-types/");
  return toList(data);
}
export async function createIncidentType(accessToken: string | undefined, payload: IncidentTypePayload): Promise<IncidentTypeRow> {
  const client = getClient(accessToken);
  const { data } = await client.post<IncidentTypeRow>("/api/v1/incident-types/", payload);
  return data;
}
export async function updateIncidentType(accessToken: string | undefined, id: string, payload: Partial<IncidentTypePayload>): Promise<IncidentTypeRow> {
  const client = getClient(accessToken);
  const { data } = await client.patch<IncidentTypeRow>(`/api/v1/incident-types/${id}/`, payload);
  return data;
}
export async function deleteIncidentType(accessToken: string | undefined, id: string): Promise<void> {
  const client = getClient(accessToken);
  await client.delete(`/api/v1/incident-types/${id}/`);
}
